/**
 * Desktop alerts for the Settlements Queue.
 *
 * While the platform is open in any tab (even minimised):
 *   - people who work the queue (Settlements, Admin) hear about new
 *     tickets: "Mark added a ticket to the Payment queue" (never their own)
 *   - whoever raised a payment or drawdown ticket hears when it's done:
 *     "Liam completed your Payment ticket" (PMs included; not when they
 *     completed it themselves)
 * Clicking an alert brings the platform forward on the queue.
 *
 * - Opt-in per browser (the toggle on the Settlements Queue page), and the
 *   browser's own permission prompt must be allowed.
 * - One tab does the checking (Web Locks), so several open tabs don't
 *   each fire the same alert. If that tab closes another takes over, and
 *   picks up from the last check so nothing is missed in between.
 * - The 10 second tick runs in a small Web Worker: browsers slow timers in
 *   background tabs to once a minute, but not worker timers.
 * - Several tickets from one person to one queue in the same check become
 *   one alert ("Mark added 5 tickets to the Payment queue").
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FLOW_LABEL, settlementsApi, type CompletedTicketAlert, type NewTicketAlert } from "@/lib/settlementsApi";

const ENABLED_KEY = "mfx_ticket_alerts";
const SINCE_KEY = "mfx_ticket_alerts_since";
const LOCK_NAME = "mfx-ticket-alerts";
const POLL_MS = 10_000;
// A handed-over "since" older than this is stale (the platform was closed);
// start fresh rather than replaying a backlog.
const RESUME_MAX_MS = 2 * 60_000;

export type AlertStatus = "unsupported" | "off" | "on" | "blocked";

export function alertsSupported(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

function readEnabled(): boolean {
  try { return localStorage.getItem(ENABLED_KEY) === "on"; } catch { return false; }
}

export function alertStatus(): AlertStatus {
  if (!alertsSupported()) return "unsupported";
  if (Notification.permission === "denied") return "blocked";
  return readEnabled() && Notification.permission === "granted" ? "on" : "off";
}

/** Turn alerts on (asks the browser for permission) or off. */
export async function setAlertsEnabled(on: boolean): Promise<AlertStatus> {
  if (!alertsSupported()) return "unsupported";
  if (on && Notification.permission !== "granted") {
    const result = await Notification.requestPermission();
    if (result !== "granted") return result === "denied" ? "blocked" : "off";
  }
  try {
    localStorage.setItem(ENABLED_KEY, on ? "on" : "off");
    if (!on) localStorage.removeItem(SINCE_KEY);
  } catch { /* private mode */ }
  window.dispatchEvent(new Event("mfx-ticket-alerts-changed"));
  if (on) {
    new Notification("Desktop alerts are on", {
      body: "You'll get Settlements Queue notifications here: new tickets if you work the queue, and when tickets you raised are completed.",
      tag: "mfx-ticket-alerts-on",
    });
  }
  return alertStatus();
}

/** Live status for the toggle, following changes in other tabs too. */
export function useAlertStatus(): AlertStatus {
  const [status, setStatus] = useState<AlertStatus>(alertStatus);
  useEffect(() => {
    const update = () => setStatus(alertStatus());
    window.addEventListener("storage", update);
    window.addEventListener("mfx-ticket-alerts-changed", update);
    window.addEventListener("focus", update);
    return () => {
      window.removeEventListener("storage", update);
      window.removeEventListener("mfx-ticket-alerts-changed", update);
      window.removeEventListener("focus", update);
    };
  }, []);
  return status;
}

function describe(group: NewTicketAlert[]): { title: string; body: string } {
  const first = group[0];
  const who = first.createdByName || "Someone";
  const queue = `${FLOW_LABEL[first.flow] || first.flow} queue`;
  const urgent = group.some((t) => t.urgent);
  if (group.length === 1) {
    const detail = [first.reference, first.description].filter(Boolean).join(" · ");
    return {
      title: `${urgent ? "Urgent: " : ""}${who} added a ticket to the ${queue}`,
      body: detail || "Open the Settlements Queue to pick it up.",
    };
  }
  const refs = group.map((t) => t.reference).filter(Boolean).slice(0, 4).join(", ");
  return {
    title: `${urgent ? "Urgent: " : ""}${who} added ${group.length} tickets to the ${queue}`,
    body: refs ? `${refs}${group.length > 4 ? "…" : ""}` : "Open the Settlements Queue to pick them up.",
  };
}

function describeDone(group: CompletedTicketAlert[]): { title: string; body: string } {
  const first = group[0];
  const who = first.completedByName || "Settlements";
  const flow = FLOW_LABEL[first.flow] || first.flow;
  if (group.length === 1) {
    const detail = [first.reference, first.description].filter(Boolean).join(" · ");
    return { title: `${who} completed your ${flow} ticket`, body: detail || "It's done." };
  }
  const refs = group.map((t) => t.reference).filter(Boolean).slice(0, 4).join(", ");
  return {
    title: `${who} completed ${group.length} of your ${flow} tickets`,
    body: refs ? `${refs}${group.length > 4 ? "…" : ""}` : "They're done.",
  };
}

function makeTicker(onTick: () => void): () => void {
  try {
    const src = `setInterval(function(){postMessage(0)},${POLL_MS});`;
    const url = URL.createObjectURL(new Blob([src], { type: "text/javascript" }));
    const worker = new Worker(url);
    worker.onmessage = onTick;
    return () => { worker.terminate(); URL.revokeObjectURL(url); };
  } catch {
    const id = window.setInterval(onTick, POLL_MS);
    return () => window.clearInterval(id);
  }
}

/** Mounted once in the staff app. Does nothing unless this user uses the
 *  queue and has alerts on in this browser. */
export function TicketAlerts({ user }: { user: any }) {
  const navigate = useNavigate();
  const status = useAlertStatus();
  const role = user?.role;
  const works = role === "mfx_settlements" || role === "mfx_admin" || user?.isSuperAdmin === true;
  const eligible = works || role === "mfx_pm";
  const active = eligible && status === "on";

  useEffect(() => {
    if (!active) return;
    let stopped = false;
    let release: (() => void) | null = null;
    let stopTicker: (() => void) | null = null;

    const run = () => {
      let since: string | null = null;
      try {
        const raw = localStorage.getItem(SINCE_KEY);
        if (raw && Date.now() - new Date(raw).getTime() < RESUME_MAX_MS) since = raw;
      } catch { /* ignore */ }
      let inFlight = false;

      const check = async () => {
        if (stopped || inFlight) return;
        inFlight = true;
        try {
          const res = await settlementsApi.newSince(since);
          since = res.now;
          try { localStorage.setItem(SINCE_KEY, res.now); } catch { /* ignore */ }
          const others = works ? res.tickets.filter((t) => String(t.createdById || "") !== String(res.me)) : [];
          const groups = new Map<string, NewTicketAlert[]>();
          for (const t of others) {
            const k = `${t.createdById || t.createdByName}|${t.flow}`;
            groups.set(k, [...(groups.get(k) || []), t]);
          }
          for (const group of groups.values()) {
            const { title, body } = describe(group);
            const n = new Notification(title, { body, tag: `mfx-ticket-${group[0].id}` });
            n.onclick = () => {
              window.focus();
              navigate("/settlements");
              n.close();
            };
          }
          const doneGroups = new Map<string, CompletedTicketAlert[]>();
          for (const t of res.completed || []) {
            const k = `${t.completedByName}|${t.flow}`;
            doneGroups.set(k, [...(doneGroups.get(k) || []), t]);
          }
          for (const group of doneGroups.values()) {
            const { title, body } = describeDone(group);
            const n = new Notification(title, { body, tag: `mfx-ticket-done-${group[0].id}` });
            n.onclick = () => {
              window.focus();
              navigate("/settlements");
              n.close();
            };
          }
        } catch { /* offline or signed out: try again next tick */ }
        finally { inFlight = false; }
      };

      check();
      stopTicker = makeTicker(check);
    };

    const locks = (navigator as any).locks;
    if (locks?.request) {
      // Only the tab holding the lock checks; it holds it until this effect
      // cleans up (alerts off, sign out, tab closed).
      locks.request(LOCK_NAME, () => new Promise<void>((resolve) => {
        release = resolve;
        if (stopped) { resolve(); return; }
        run();
      })).catch(() => { /* ignore */ });
    } else {
      run();
    }

    return () => {
      stopped = true;
      stopTicker?.();
      release?.();
    };
  }, [active, works, navigate]);

  return null;
}

/** The on/off switch shown on the Settlements Queue page. */
export function TicketAlertsToggle() {
  const status = useAlertStatus();
  const [busy, setBusy] = useState(false);
  if (status === "unsupported") return null;
  const on = status === "on";
  const blocked = status === "blocked";
  const toggle = async () => {
    setBusy(true);
    try { await setAlertsEnabled(!on); } finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy || blocked}
      title={blocked
        ? "Notifications are blocked for this site. Allow them in your browser's site settings, then refresh."
        : on ? "Desktop notifications for the Settlements Queue are on. Click to turn off."
          : "Get desktop notifications for new tickets and when your tickets are completed"}
      className={
        "h-10 px-3.5 rounded-xl text-body-sm font-medium border inline-flex items-center gap-2 focus-ring disabled:opacity-50 " +
        (on ? "border-status-success/40 bg-status-success-bg text-status-success" : "border-surface-border bg-white text-dark-600 hover:bg-surface-secondary")
      }
    >
      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
        <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        {!on && <line x1="3" y1="3" x2="21" y2="21" />}
      </svg>
      {blocked ? "Alerts blocked" : on ? "Desktop alerts on" : "Desktop alerts off"}
    </button>
  );
}
